import { ExploreCostCharts } from '@/components/classic-guide/ExploreCostCharts';
import { FormulaCard } from '@/components/formulas/FormulaCard';
import { NavIcon } from '@/components/icons/NavIcon';
import { Card } from '@/components/ui/card';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

const stackedCaps = [
  {
    title: 'Built land',
    body: 'You cannot explore unless more than half of your land is built. If too much land is barren, exploration is blocked.',
  },
  {
    title: 'Gold, soldiers and pool',
    body: 'Your order is limited by the gold you have, the soldiers you have, and the kingdom explore pool available at the time you send it.',
  },
  {
    title: 'Current explore queue',
    body: 'Acres already being explored count against how much more you may send.',
  },
  {
    title: 'Kingdom explore limit',
    body: 'The Monarch or Co-Monarch may set a kingdom explore limit. Provinces may not explore past that size while the limit is active.',
  },
  {
    title: 'War replacement limit',
    body: 'During war, you may only explore enough land to return to the size you were when the war began. If you are at or above that size, you cannot explore. War exploration costs twice the usual gold and soldiers.',
  },
];

const slottedRules = [
  'Rebuild blocks exploration outside protection, and the kingdom pool does not grow while the kingdom remains in Rebuild.',
  'In Jubilant, Widen Explore halves explore time but uses twice as many acres from the kingdom pool.',
  'The kingdom pool is stored in hundredths of an acre, so the shown value is the stored value divided by 100.',
  'Explored acres count toward total explored land for later cost calculations before the land arrives.',
];

function DataTable({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#d4af37]/25">
      <table className="min-w-full divide-y divide-[#d4af37]/20 text-left text-base">
        {children}
      </table>
    </div>
  );
}

function TableHeader({ children }: { children: ReactNode }) {
  return <th className="px-4 py-3 font-semibold text-[#e6c547]">{children}</th>;
}

function TableCell({ children }: { children: ReactNode }) {
  return <td className="px-4 py-3 text-[#f0f0f0]">{children}</td>;
}

export const Route = createFileRoute('/classic-guide/exploration')({
  component: ClassicGuideExplorationPage,
});

function ClassicGuideExplorationPage() {
  return (
    <div className="space-y-5">
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <NavIcon
            code="explore"
            width={72}
            height={72}
            className="shrink-0 overflow-hidden rounded-xl border border-[#d4af37]/25 shadow-[0_0_18px_rgba(212,175,55,0.16)]"
          />
          <div className="min-w-0">
            <h2 className="font-cinzel text-3xl text-[#f0f0f0]">Exploration</h2>
            <p className="mt-1 text-base leading-7 text-[#b8b8b8]">
              Exploration rules, kingdom pool growth, kingdom limits and age limits.
            </p>
          </div>
        </div>
      </Card>

      <Card className="space-y-5 p-6">
        <div className="space-y-4">
          <h3 className="font-cinzel text-2xl text-[#e6c547]">Explore Limits</h3>
          <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
            This game does not set an age-wide explore ceiling. The limits that do apply are the kingdom explore limit, the current queue, the war replacement limit, and the Jubilant and Rebuild rules.
          </p>
          <ul className="space-y-2 rounded-lg border border-[#d4af37]/15 bg-[#050505]/50 px-5 py-4 text-[1.0625rem] leading-7 text-[#e8dfcf]">
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>War exploration is replacement-only. You may explore up to the land you had when the war began, but not beyond it.</span></li>
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>Exploration during war costs twice the usual gold and soldiers per acre.</span></li>
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>During Jubilant, Widen Explore can trade extra pool acres for a faster journey.</span></li>
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>Rebuild stance forbids exploration and also stops pool growth, making it a true consolidation stance rather than a growth stance.</span></li>
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>Defection, banishment, and province death can refund explored land back into a kingdom pool, so pool depth is partly political history, not just natural growth.</span></li>
          </ul>

          <div className="grid gap-3 sm:grid-cols-2">
            <Link to="/jubilant">
              <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
                <h4 className="font-cinzel text-xl text-[#f0f0f0]">Jubilant</h4>
                <p className="mt-2 text-sm text-[#b8b8b8]">Recovery rules and Widen Explore.</p>
              </Card>
            </Link>
            <Link to="/classic-guide/$pageId" params={{ pageId: 'economics-growth' }}>
              <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
                <h4 className="font-cinzel text-xl text-[#f0f0f0]">Economics &amp; Growth</h4>
                <p className="mt-2 text-sm text-[#b8b8b8]">Economy, barren land and explore cost.</p>
              </Card>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
